import React, { useCallback, useMemo } from "react";
import {
  handleCreateResponse,
  handleDeleteResponse,
  handleUpdateResponse,
  useCreateAgentGroupsMutation,
  useDeleteAgentGroupsMutation,
  useGetAgentGroupsQuery,
  useUpdateAgentGroupsMutation,
} from "./agentGroupsApi";
import columnDefinition from "./columnDefinition";
import { useSelector } from "react-redux";
import { tenantSelector } from "@/react/features/customer/customerSlice";
import TableComponent from "@/react/components/table/TableComponent";
import schema from "./schema";
import { useGetProfileQuery } from "../profile/profileApi";
import agentGroupFlagColumnMapper from "./utils/agentGroupFlagColumnMapper";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { useTable } from "@/react/components/table/useTable";
import * as yup from "yup";
import { appendIdFromKey } from "@/react/components/table/useTableMutations";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import TableWrapper from "@/react/features/styled/TableWrapper";
import PageTitle from "@/react/components/PageTitle";

export function AgentGroupsPage() {
  const tenant = useSelector(tenantSelector);
  const tenantName = tenant.tenantId;
  const { data: profile } = useGetProfileQuery(tenantName);
  const {
    data: initialData,
    isFetching: isLoading,
    error,
    refetch,
  } = useGetAgentGroupsQuery(tenantName);

  const agentGroupsColumns = useMemo(() => {
    if (!profile) {
      return columnDefinition();
    }

    return columnDefinition().map(agentGroupFlagColumnMapper(profile.flags));
  }, [profile]);

  useErrorNotification(error);

  const [createAgentGroups, { isLoading: isCreating }] =
    useCreateAgentGroupsMutation();
  const [updateAgentGroups, { isLoading: isUpdating }] =
    useUpdateAgentGroupsMutation();
  const [deleteAgentGroups, { isLoading: isDeleting }] =
    useDeleteAgentGroupsMutation();

  const { table, filters, state, mutations } = useTable(
    initialData,
    agentGroupsColumns,
    schema,
    schema.fields.id.spec.key
  );

  const {
    rowsToCreate,
    rowsToUpdate,
    rowsToDelete,
    resetData,

    setData,
    setRowsToCreate,
    setRowsToUpdate,
    setRowsToDelete,
  } = mutations;

  const isDirty =
    rowsToCreate.size > 0 || rowsToUpdate.size > 0 || rowsToDelete.size > 0;

  const onSave = useCallback(() => {
    if (rowsToCreate.size > 0) {
      createAgentGroups({
        tenantName,
        agentGroups: appendIdFromKey(
          Array.from(rowsToCreate.values()),
          schema.fields.id.spec.key
        ),
      })
        .unwrap()
        .then(handleCreateResponse({ rowsToCreate, setRowsToCreate, setData }))
        .catch(errorNotification);
    }

    if (rowsToUpdate.size > 0) {
      updateAgentGroups({
        tenantName,
        agentGroups: Array.from(rowsToUpdate.values()),
      })
        .unwrap()
        .then(handleUpdateResponse({ rowsToUpdate, setRowsToUpdate, setData }))
        .catch(errorNotification);
    }

    if (rowsToDelete.size > 0) {
      deleteAgentGroups({
        tenantName,
        agentGroupIds: Array.from(rowsToDelete.keys()),
      })
        .unwrap()
        .then(handleDeleteResponse({ rowsToDelete, setRowsToDelete, setData }))
        .catch(errorNotification);
    }
  }, [rowsToCreate, rowsToUpdate, rowsToDelete]);

  const isTableLoading = isLoading || isCreating || isUpdating || isDeleting;

  return (
    <PageContentContainer>
      <PageTitle>Agent Groups</PageTitle>
      <TableWrapper>
        <TableComponent
          title="Agent Groups"
          schema={schema}
          isDirty={isDirty}
          isLoading={isTableLoading}
          onSave={onSave}
          onCancel={resetData}
          onRefresh={refetch}
          table={table}
          filters={filters}
          mutations={mutations}
          state={state}
          fileActions={{
            identifierColumnId: yup.reach(schema, "name").spec.key,
            enableUpdate: true,
            enableAdd: true,
            enableDownload: true,
          }}
        />
      </TableWrapper>
    </PageContentContainer>
  );
}
